import type { QuotaCycle } from "./quota";
import { quantile } from "./series";
import type { EndpointSnapshot, HealthState } from "./types/api";

/** Network conditions, ordered roughly from calm to severe. */
export type Condition = "clear" | "cloudy" | "showers" | "rain" | "storm" | "fog" | "unknown";
export type EndpointGroup = "cn" | "global" | "other";
/** `failed` is one bad probe; `down` means most of the last few probes failed too. */
export type EndpointStatus = "ok" | "slow" | "failed" | "down" | "unknown";

export interface EndpointInsight {
  id: string;
  name: string;
  group: EndpointGroup;
  status: EndpointStatus;
  /** Latest total time; null when the latest probe failed or never ran. */
  latency: number | null;
  /** 24-hour figures from the API's baseline; null when it has none for this endpoint. */
  median: number | null;
  p10: number | null;
  p90: number | null;
  p98: number | null;
  availability: number | null;
  failures: number | null;
  /** Consecutive failed probes, which run once a minute. */
  failingMinutes: number;
  /** Recent latency over the 24-hour median, set only while the endpoint counts as slow. */
  slowFactor: number | null;
}

const GROUP_BY_ID: Record<string, EndpointGroup> = {
  ct: "cn",
  cu: "cn",
  cm: "cn",
  baidu: "cn",
  "aws-tokyo": "global",
  cloudflare: "global",
  google: "global",
  github: "global",
  openai: "global"
};

export const GROUP_LABELS: Record<EndpointGroup, string> = {
  cn: "中国大陆",
  global: "国际",
  other: "其他"
};

export function endpointGroup(id: string): EndpointGroup {
  return GROUP_BY_ID[id] ?? "other";
}

/** Fewer probes than this say too little about what is normal for an endpoint. */
const MIN_BASELINE_SAMPLES = 30;

/**
 * Judge one endpoint against its own last 24 hours, as summarised by the API. An endpoint
 * is "slow" only when the median of its newest probes clears both its usual range and its
 * median by a real margin, so a single spike does not count.
 */
export function buildInsight(endpoint: EndpointSnapshot): EndpointInsight {
  const baseline = endpoint.baseline ?? null;
  const sampled = endpoint.success != null;
  const ok = endpoint.success === 1;

  let status: EndpointStatus = !sampled ? "unknown" : ok ? "ok" : "failed";
  let slowFactor: number | null = null;
  if (
    ok &&
    baseline != null &&
    baseline.samples >= MIN_BASELINE_SAMPLES &&
    baseline.median_ms != null &&
    baseline.p90_ms != null &&
    baseline.recent_median_ms != null
  ) {
    const usual = baseline.median_ms;
    const now = baseline.recent_median_ms;
    if (now > baseline.p90_ms && now >= usual * 1.5 && now - usual >= 80) {
      status = "slow";
      slowFactor = now / usual;
    }
  }
  if (status === "failed" && baseline != null && baseline.recent_failures >= 3) status = "down";

  return {
    id: endpoint.id,
    name: endpoint.name,
    group: endpointGroup(endpoint.id),
    status,
    latency: ok ? endpoint.total_ms : null,
    median: baseline?.median_ms ?? null,
    p10: baseline?.p10_ms ?? null,
    p90: baseline?.p90_ms ?? null,
    p98: baseline?.p98_ms ?? null,
    availability: baseline?.availability ?? null,
    failures: baseline?.failures ?? null,
    failingMinutes: ok ? 0 : Math.max(1, baseline?.failing_streak ?? 1),
    slowFactor
  };
}

export interface WeatherInput {
  state: HealthState;
  ageSeconds: number | null;
  insights: ReadonlyArray<EndpointInsight>;
  conntrackPercent: number | null;
  tableFullRecent: number;
  /** Hour of day at the observation point, 0-23. */
  stationHour: number;
}

export interface Weather {
  condition: Condition;
  /** After dark at the observation point: the sun becomes a moon. */
  night: boolean;
  word: string;
  sentence: string;
  /** Advisory ids the sentence already spells out, so they are not listed a second time. */
  covers: ReadonlyArray<string>;
}

/**
 * Every character the hero may set in its display face. The bundled font is a subset of
 * Noto Serif SC holding exactly these; scripts/subset-weather-font.py rebuilds it from this
 * line, so extend it (and rerun the script) before introducing a word with a new character.
 */
export const DISPLAY_GLYPHS = "晴多云阴阵雨局部有雷暴雾霾雪风大小中转间待测观零星";

export const CONDITION_WORDS: Record<Condition, string> = {
  clear: "晴",
  cloudy: "多云",
  showers: "阵雨",
  rain: "局部有雨",
  storm: "雷暴",
  fog: "雾",
  unknown: "待测"
};

function listNames(items: ReadonlyArray<EndpointInsight>): string {
  if (items.length <= 2) return items.map((item) => item.name).join("、");
  return `${items[0].name} 等 ${items.length} 个端点`;
}

function groupMedian(insights: ReadonlyArray<EndpointInsight>, group?: EndpointGroup): number | null {
  const values = insights
    .filter((item) => (group ? item.group === group : true) && item.latency != null)
    .map((item) => item.latency as number)
    .sort((a, b) => a - b);
  return quantile(values, 0.5);
}

export function describeAge(seconds: number): string {
  if (seconds < 90) return `${Math.round(seconds)} 秒`;
  if (seconds < 5400) return `${Math.round(seconds / 60)} 分钟`;
  if (seconds < 129_600) return `${Math.round(seconds / 3600)} 小时`;
  return `${Math.round(seconds / 86400)} 天`;
}

function ms(value: number): string {
  return `${Math.round(value).toLocaleString("en-US")} ms`;
}

/** Turn the current observation into a weather word and one plain sentence. */
export function deriveWeather(input: WeatherInput): Weather {
  const night = input.stationHour < 6 || input.stationHour >= 18;
  const make = (condition: Condition, sentence: string, covers: string[] = []): Weather => ({
    condition,
    night,
    word: CONDITION_WORDS[condition],
    sentence,
    covers
  });
  const { insights } = input;
  const total = insights.length;

  if (input.state === "empty" || (total === 0 && input.state !== "stale")) {
    return make("unknown", "采集器首次运行后，这里会出现网络天气。");
  }
  if (input.state === "stale") {
    const age = input.ageSeconds == null ? "一段时间" : ` ${describeAge(input.ageSeconds)}`;
    return make("fog", `观测数据已有${age}没有更新，采集器可能已经停止。`);
  }

  const failing = insights.filter((item) => item.status === "failed" || item.status === "down");
  const down = insights.filter((item) => item.status === "down");
  const slow = insights.filter((item) => item.status === "slow");
  const healthy = total - failing.length;
  const conntrack = input.conntrackPercent ?? 0;

  if (failing.length >= Math.max(2, Math.ceil(total / 2))) {
    const scope = failing.length === total ? "全部" : `中有 ${failing.length} 个`;
    return make("storm", `${total} 个端点${scope}不可达，出口网络可能出现故障。`);
  }

  if (down.length) {
    const duration = down.length === 1 ? `已连续 ${down[0].failingMinutes} 分钟不可达` : "持续不可达";
    const blips = failing.length - down.length;
    const rest = blips
      ? `另有 ${blips} 个端点刚刚探测失败。`
      : healthy === 1
        ? "另一个端点正常。"
        : `其余 ${healthy} 个端点正常。`;
    return make("rain", `${listNames(down)} ${duration}，${rest}`);
  }
  if (input.tableFullRecent > 0) {
    return make(
      "rain",
      `端点全部可达，但连接跟踪表最近满过 ${input.tableFullRecent} 次，新连接可能被内核丢弃。`,
      ["conntrack-full"]
    );
  }
  if (conntrack >= 85) {
    return make(
      "rain",
      `端点全部可达，但连接跟踪表使用率已达 ${conntrack.toFixed(0)}%，接近上限。`,
      ["conntrack"]
    );
  }

  if (failing.length) {
    const rest = healthy === 1 ? "另一个端点正常。" : `其余 ${healthy} 个端点正常。`;
    return make("showers", `${listNames(failing)} 刚刚探测失败，${rest}`);
  }

  if (slow.length) {
    const sentence =
      slow.length === total
        ? "所有端点都比平时慢。"
        : `${listNames(slow)} 比平时慢，其余端点正常。`;
    return make("cloudy", sentence);
  }
  if (conntrack >= 70) {
    return make("cloudy", `端点全部畅通，但连接跟踪表使用率偏高（${conntrack.toFixed(0)}%）。`, [
      "conntrack"
    ]);
  }

  const globalMedian = groupMedian(insights, "global");
  const cnMedian = groupMedian(insights, "cn");
  const overall = groupMedian(insights);
  const lead = total === 1 ? `${insights[0].name} 畅通` : `${total} 个端点全部畅通`;
  if (globalMedian != null && cnMedian != null) {
    return make(
      "clear",
      `${lead}，国际方向中位延迟 ${ms(globalMedian)}，中国大陆方向 ${ms(cnMedian)}。`
    );
  }
  return make("clear", overall == null ? `${lead}。` : `${lead}，中位延迟 ${ms(overall)}。`);
}

export interface Advisory {
  id: string;
  level: "warning" | "critical";
  text: string;
}

export interface AdvisoryInput {
  refreshError: string | null;
  tableFullRecent: number;
  alertHoldSeconds: number;
  conntrackPercent: number | null;
  diskPercent: number | null;
  quota: QuotaCycle | null;
  collectorError: string | null;
}

/** Things worth a line under the forecast. Nothing is listed while everything is normal. */
export function deriveAdvisories(input: AdvisoryInput): Advisory[] {
  const advisories: Advisory[] = [];
  if (input.refreshError) {
    advisories.push({
      id: "refresh",
      level: "warning",
      text: `自动刷新失败，当前显示的是最近一次成功读取的数据（${input.refreshError}）。`
    });
  }
  if (input.collectorError) {
    advisories.push({
      id: "collector",
      level: "warning",
      text: `采集器报告错误：${input.collectorError}`
    });
  }
  if (input.tableFullRecent > 0) {
    advisories.push({
      id: "conntrack-full",
      level: "critical",
      text: `最近 ${Math.round(input.alertHoldSeconds / 60)} 分钟内出现 ${input.tableFullRecent} 次 conntrack table full，新连接可能被内核丢弃。`
    });
  }
  const conntrack = input.conntrackPercent ?? 0;
  if (conntrack >= 85) {
    advisories.push({
      id: "conntrack",
      level: "critical",
      text: `连接跟踪表使用率 ${conntrack.toFixed(0)}%，接近上限。`
    });
  } else if (conntrack >= 70) {
    advisories.push({
      id: "conntrack",
      level: "warning",
      text: `连接跟踪表使用率 ${conntrack.toFixed(0)}%，需要关注。`
    });
  }
  // Usage that is merely high late in a cycle is not news; running out before the reset is.
  const quota = input.quota;
  if (quota) {
    const projected = quota.projected;
    if (quota.percent >= 90) {
      advisories.push({
        id: "quota",
        level: "critical",
        text: `本周期流量已用 ${quota.percent.toFixed(0)}%，还有 ${quota.daysLeft.toFixed(1)} 天重置。`
      });
    } else if (projected != null && projected >= quota.quota) {
      advisories.push({
        id: "quota",
        level: "critical",
        text: "按当前速度，本周期流量会在重置前用完。"
      });
    } else if (projected != null && projected >= quota.quota * 0.9) {
      advisories.push({
        id: "quota",
        level: "warning",
        text: "按当前速度，本周期流量会用到额度的九成以上。"
      });
    }
  }
  if ((input.diskPercent ?? 0) >= 90) {
    advisories.push({
      id: "disk",
      level: "warning",
      text: `磁盘已用 ${(input.diskPercent as number).toFixed(0)}%。`
    });
  }
  return advisories;
}
